import React, { useState } from 'react';
import { Sparkles, ArrowRight, Check, Loader, Lightbulb, AlertCircle, Database } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

export default function AICampaignGenerator({ onCampaignGenerated }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const [step, setStep] = useState(1); // 1: prompt, 2: keywords review
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [editableCampaignData, setEditableCampaignData] = useState(null);
  const [keywordStats, setKeywordStats] = useState(null);

  const examples = [
    "I want to sell bike tours in Valencia for Dutch tourists, budget €50/day",
    "Electric scooters for students in Amsterdam, €30 daily budget",
    "Yoga retreats in Bali targeting wellness seekers, budget €100/day"
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    
    setLoading(true);
    setError(null);
    setLoadingMessage('🤖 Analyzing your campaign description...');
    
    try {
      // Get auth token
      const token = await getToken();
      
      // Get selected account from localStorage
      const savedAccount = localStorage.getItem('selectedGoogleAdsAccount');
      let accountId = null;
      if (savedAccount) {
        try {
          const account = JSON.parse(savedAccount);
          accountId = account.id;
        } catch (e) {
          console.error('Failed to parse saved account:', e);
        }
      }
      
      if (!accountId) {
        setError('Please select a Google Ads account first from the sidebar.');
        setLoading(false);
        return;
      }
      
      // Step 1: Parse prompt with AI
      setLoadingMessage('🧠 Understanding your business and target audience...');
      
      const parseResponse = await fetch(`${API_URL}/ai-campaign/parse-prompt`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Account-Id': accountId
        },
        body: JSON.stringify({ prompt })
      });
      const parseData = await parseResponse.json();
      
      if (!parseData.success) {
        setError('Failed to parse prompt: ' + parseData.error);
        return;
      }
      
      setExtracted(parseData.extracted);
      
      // Step 2: Generate campaign with REAL keywords from Keyword Planner
      setLoadingMessage('🔍 Fetching real keywords from Google Keyword Planner...');
      
      const generateResponse = await fetch(`${API_URL}/ai-campaign/generate-with-keywords`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Account-Id': accountId
        },
        body: JSON.stringify({ 
          prompt,
          extracted: parseData.extracted 
        })
      });
      const generateData = await generateResponse.json();
      
      if (!generateData.success) {
        // Show helpful error with suggestion
        const errorMessage = generateData.error || 'Failed to generate campaign';
        const suggestion = generateData.suggestion || '';
        setError(
          <div>
            <p><strong>Could not generate keywords:</strong></p>
            <p style={{ marginTop: 8 }}>{errorMessage}</p>
            {suggestion && (
              <p style={{ marginTop: 12, color: '#8b5cf6' }}>
                💡 {suggestion}
              </p>
            )}
          </div>
        );
        return;
      }
      
      setCampaignData(generateData.campaign);
      setSelectedKeywords(generateData.campaign.keywords.map(kw => kw.keyword));
      setEditableCampaignData({
        name: generateData.campaign.name,
        budget: generateData.campaign.budget,
        currency: generateData.campaign.currency
      });
      setKeywordStats(generateData.keywordStats);
      setStep(2);
      
    } catch (error) {
      console.error('Error:', error);
      setError('Error generating campaign: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleProceedToCampaignBuilder = () => {
    // Filter selected keywords
    const filteredKeywords = campaignData.keywords.filter(kw => 
      selectedKeywords.includes(kw.keyword)
    );
    
    // Pass data to Campaign Builder (using editable name and budget)
    onCampaignGenerated({
      ...campaignData,
      name: editableCampaignData.name,
      budget: editableCampaignData.budget,
      keywords: filteredKeywords,
      sourcePrompt: prompt
    });
  };

  return (
    <div style={{ padding: 40, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 40, textAlign: 'center' }}>
        <h1 style={{ 
          color: theme.colors.text.primary, 
          fontSize: 36, 
          fontWeight: 700, 
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12
        }}>
          <Sparkles size={40} color="#8b5cf6" />
          AI Campaign Generator
        </h1>
        <p style={{ color: theme.colors.text.tertiary, fontSize: 18 }}>
          Describe your campaign in natural language, we'll handle the rest
        </p>
      </div>

      {step === 1 && (
        <>
          {/* Prompt Input */}
          <div style={{
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: 20,
            padding: 40,
            marginBottom: 30
          }}>
            <label style={{ 
              color: theme.colors.text.primary, 
              fontSize: 18, 
              fontWeight: 600, 
              display: 'block',
              marginBottom: 16
            }}>
              Describe your campaign
            </label>
            
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., I want to sell bike tours in Valencia for Dutch tourists, budget €50/day"
              rows={4}
              style={{
                width: '100%',
                padding: 20,
                borderRadius: 12,
                border: '2px solid rgba(139,92,246,0.3)',
                background: theme.colors.bg.tertiary,
                color: theme.colors.text.primary,
                fontSize: 16,
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            
            <button
              onClick={handleGenerate}
              disabled={loading || !prompt.trim()}
              style={{
                marginTop: 20,
                background: loading ? 'rgba(139,92,246,0.5)' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                border: 'none',
                color: 'white',
                padding: '16px 32px',
                borderRadius: 12,
                cursor: loading || !prompt.trim() ? 'not-allowed' : 'pointer',
                fontSize: 16,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}
            >
              {loading ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  {loadingMessage || 'Generating...'}
                </>
              ) : (
                <>
                  <Sparkles size={20} />
                  Generate Campaign
                  <ArrowRight size={20} />
                </>
              )}
            </button>
            
            {/* Data source info */}
            <div style={{
              marginTop: 16,
              padding: 12,
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <Database size={18} color="#10b981" />
              <span style={{ color: theme.colors.text.secondary, fontSize: 13 }}>
                Uses <strong style={{ color: '#10b981' }}>real Google Keyword Planner data</strong> from your connected account
              </span>
            </div>

            {error && (
              <div style={{
                marginTop: 20,
                padding: 16,
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 12,
                color: '#ef4444',
                fontSize: 14
              }}>
                {error}
              </div>
            )}
          </div>

          {/* Examples */}
          <div style={{
            background: 'rgba(139,92,246,0.1)',
            border: '1px solid rgba(139,92,246,0.3)',
            borderRadius: 16,
            padding: 24
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 10,
              marginBottom: 16
            }}>
              <Lightbulb size={20} color="#8b5cf6" />
              <span style={{ color: '#8b5cf6', fontWeight: 600 }}>Examples</span>
            </div>
            {examples.map((example, idx) => (
              <div
                key={idx}
                onClick={() => setPrompt(example)}
                style={{
                  padding: '12px 16px',
                  background: theme.colors.bg.tertiary,
                  borderRadius: 8,
                  marginBottom: idx < examples.length - 1 ? 8 : 0,
                  cursor: 'pointer',
                  color: theme.colors.text.secondary,
                  fontSize: 14,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(139,92,246,0.2)';
                  e.currentTarget.style.color = theme.colors.text.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.colors.bg.tertiary;
                  e.currentTarget.style.color = theme.colors.text.secondary;
                }}
              >
                {example}
              </div>
            ))}
          </div>
        </>
      )}

      {step === 2 && campaignData && (
        <>
          {/* Campaign Overview */}
          <div style={{
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: 20,
            padding: 30,
            marginBottom: 30
          }}>
            <h3 style={{ color: theme.colors.text.primary, fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
              Campaign Overview
            </h3>
            {editableCampaignData && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20 }}>
                {/* Campaign Name */}
                <div>
                  <label style={{ color: theme.colors.text.tertiary, fontSize: 12, marginBottom: 8, display: 'block' }}>
                    Campaign Name
                  </label>
                  <input 
                    type="text"
                    value={editableCampaignData.name}
                    onChange={(e) => {
                      setEditableCampaignData({...editableCampaignData, name: e.target.value});
                    }}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '2px solid rgba(139,92,246,0.3)', 
                      background: theme.colors.bg.tertiary, 
                      color: theme.colors.text.primary,
                      fontSize: 16,
                      fontWeight: 600
                    }}
                  />
                </div>

                {/* Budget */}
                <div>
                  <label style={{ color: theme.colors.text.tertiary, fontSize: 12, marginBottom: 8, display: 'block' }}>
                    Daily Budget ({editableCampaignData.currency})
                  </label>
                  <input 
                    type="number"
                    value={editableCampaignData.budget}
                    onChange={(e) => {
                      setEditableCampaignData({...editableCampaignData, budget: parseFloat(e.target.value)});
                    }}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '2px solid rgba(139,92,246,0.3)', 
                      background: theme.colors.bg.tertiary, 
                      color: theme.colors.text.primary,
                      fontSize: 16,
                      fontWeight: 600
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Keywords Selection */}
          <div style={{
            background: theme.colors.bg.secondary,
            border: `1px solid ${theme.colors.border.primary}`,
            borderRadius: 20,
            padding: 30,
            marginBottom: 30
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: theme.colors.text.primary, fontSize: 20, fontWeight: 600, margin: 0 }}>
                Selected Keywords ({selectedKeywords.length}/{campaignData.keywords.length})
              </h3>
              
              {/* Data source badge */}
              {keywordStats?.dataSource && (
                <span style={{
                  background: keywordStats.dataSource.includes('Real') 
                    ? 'rgba(16, 185, 129, 0.2)' 
                    : 'rgba(139, 92, 246, 0.2)',
                  color: keywordStats.dataSource.includes('Real') ? '#10b981' : '#a78bfa',
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <Database size={14} />
                  {keywordStats.dataSource.includes('Real') ? '✓ Real Google Ads Data' : '✨ AI Generated'}
                </span>
              )}
            </div>
            
            <p style={{ color: theme.colors.text.tertiary, fontSize: 14, marginBottom: 16 }}>
              Review and adjust the auto-selected keywords. Check/uncheck to customize.
            </p>
            
            {/* Stats summary */}
            {keywordStats && (
              <div style={{
                display: 'flex',
                gap: 20,
                marginBottom: 24,
                padding: 16,
                background: theme.colors.bg.tertiary,
                borderRadius: 12
              }}>
                <div>
                  <div style={{ color: theme.colors.text.tertiary, fontSize: 11, marginBottom: 4 }}>Total Found</div>
                  <div style={{ color: theme.colors.text.primary, fontSize: 18, fontWeight: 600 }}>{keywordStats.total}</div>
                </div>
                <div>
                  <div style={{ color: theme.colors.text.tertiary, fontSize: 11, marginBottom: 4 }}>Selected</div>
                  <div style={{ color: '#10b981', fontSize: 18, fontWeight: 600 }}>{keywordStats.selected}</div>
                </div>
                <div>
                  <div style={{ color: theme.colors.text.tertiary, fontSize: 11, marginBottom: 4 }}>Avg. Monthly Searches</div>
                  <div style={{ color: '#8b5cf6', fontSize: 18, fontWeight: 600 }}>
                    {keywordStats.avgSearchVolume?.toLocaleString() || 0}
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
              {campaignData.keywords.map((kw, idx) => (
                <KeywordCard
                  key={idx}
                  keyword={kw}
                  isSelected={selectedKeywords.includes(kw.keyword)}
                  onToggle={() => {
                    setSelectedKeywords(prev =>
                      prev.includes(kw.keyword)
                        ? prev.filter(k => k !== kw.keyword)
                        : [...prev, kw.keyword]
                    );
                  }}
                />
              ))}
            </div>
          </div>

          {/* Proceed Button */}
          <button
            onClick={handleProceedToCampaignBuilder}
            disabled={selectedKeywords.length === 0}
            style={{
              width: '100%',
              background: selectedKeywords.length > 0 
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'rgba(255,255,255,0.1)',
              border: 'none',
              color: 'white',
              padding: '18px',
              borderRadius: 12,
              cursor: selectedKeywords.length > 0 ? 'pointer' : 'not-allowed',
              fontSize: 18,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12
            }}
          >
            <Check size={24} />
            Continue to Campaign Builder ({selectedKeywords.length} keywords)
            <ArrowRight size={24} />
          </button>
        </>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const InfoCard = ({ label, value }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      background: theme.colors.bg.tertiary,
      borderRadius: 12,
      padding: 16
    }}>
      <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ color: theme.colors.text.primary, fontSize: 16, fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
};

const KeywordCard = ({ keyword, isSelected, onToggle }) => {
  const { theme } = useTheme();
  return (
    <div
      onClick={onToggle}
      style={{
        background: isSelected ? 'rgba(139,92,246,0.2)' : theme.colors.bg.tertiary,
        border: `2px solid ${isSelected ? '#8b5cf6' : theme.colors.border.primary}`,
        borderRadius: 12,
        padding: 16,
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: 15 }}>
          {keyword.keyword}
        </span>
        {isSelected && <Check size={18} color="#8b5cf6" />}
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
        <span style={{ color: theme.colors.text.tertiary }}>
          {keyword.searchVolume?.toLocaleString()} searches
        </span>
        <span style={{ color: theme.colors.text.tertiary }}>
          {keyword.competition}
        </span>
      </div>
    </div>
  );
};

